/**
 * Annonce réseau / bandeau global (SuperAdmin, brique A) — LOGIQUE PURE.
 *
 * Un seul message réseau à la fois, piloté par le super_admin, affiché en bandeau
 * sur tous les sites (client, tablette, back-office) : panne, maintenance, déploiement, info.
 * Aucune I/O ici (cf. announcementsService.ts). Singleton côté base (cf. sql/21).
 */

export type AnnouncementSeverity = 'info' | 'maintenance' | 'outage';

export interface NetworkAnnouncement {
  message: string;
  severity: AnnouncementSeverity;
  isActive: boolean;
  /** Début d'affichage ISO (null = tout de suite). */
  startsAt: string | null;
  /** Fin d'affichage ISO (null = jusqu'à désactivation). */
  endsAt: string | null;
}

/** Métadonnées d'affichage par sévérité (emoji, libellé, couleurs du bandeau). */
export const SEVERITY_META: Record<
  AnnouncementSeverity,
  { emoji: string; label: string; bg: string; fg: string }
> = {
  info: { emoji: '📣', label: 'Information', bg: '#2563EB', fg: '#FFFFFF' },
  maintenance: { emoji: '🛠️', label: 'Maintenance', bg: '#F59E0B', fg: '#1F2937' },
  outage: { emoji: '⛔', label: 'Panne', bg: '#DC2626', fg: '#FFFFFF' },
};

export const ANNOUNCEMENT_SEVERITIES: AnnouncementSeverity[] = ['info', 'maintenance', 'outage'];

/** Annonce vide par défaut (désactivée). */
export const EMPTY_ANNOUNCEMENT: NetworkAnnouncement = {
  message: '',
  severity: 'info',
  isActive: false,
  startsAt: null,
  endsAt: null,
};

/** La sévérité est-elle connue ? (garde-fou contre des données legacy). */
export function isValidSeverity(s: unknown): s is AnnouncementSeverity {
  return typeof s === 'string' && (ANNOUNCEMENT_SEVERITIES as string[]).includes(s);
}

/** Normalise une ligne `network_announcements` en NetworkAnnouncement (tolérant). */
export function toAnnouncement(row: {
  message?: string | null;
  severity?: string | null;
  is_active?: boolean | null;
  starts_at?: string | null;
  ends_at?: string | null;
}): NetworkAnnouncement {
  return {
    message: row.message ?? '',
    severity: isValidSeverity(row.severity) ? row.severity : 'info',
    isActive: row.is_active === true,
    startsAt: row.starts_at ?? null,
    endsAt: row.ends_at ?? null,
  };
}

/**
 * L'annonce doit-elle s'afficher MAINTENANT ?
 * Active, non vide, et dans la fenêtre [startsAt, endsAt] (bornes nulles = ouvertes).
 */
export function isAnnouncementLive(a: NetworkAnnouncement, now: Date = new Date()): boolean {
  if (!a.isActive || a.message.trim() === '') return false;
  const t = now.getTime();
  if (a.startsAt) {
    const s = new Date(a.startsAt).getTime();
    if (!Number.isNaN(s) && t < s) return false;
  }
  if (a.endsAt) {
    const e = new Date(a.endsAt).getTime();
    if (!Number.isNaN(e) && t > e) return false;
  }
  return true;
}

/** Style du bandeau (couleurs selon la sévérité), prêt pour un `style={}`. */
export function bannerStyle(severity: AnnouncementSeverity): { background: string; color: string } {
  const meta = SEVERITY_META[severity];
  return { background: meta.bg, color: meta.fg };
}

/** Texte complet du bandeau (emoji + message), prêt à afficher. */
export function bannerText(a: NetworkAnnouncement): string {
  return `${SEVERITY_META[a.severity].emoji} ${a.message.trim()}`;
}
